import React, { useEffect, useMemo, useState } from 'react';
import { Alert } from 'antd';
import { useSelected } from '../../providers/selectedContext';
import { useAppContext } from '../../providers/AppContext';
import { ProcessingLoader, Tab, TabList, TabPanel, TabPanels, Tabs } from '../../design-system';
import {
  getGheAnalogueTables,
  getGheInputs,
  getGheResults,
  runGheUnseenInference
} from '../../api/data';
import {
  GheInputSession,
  ReservoirConditions,
  RockId,
  TargetRockInput
} from '../../@types/gheInputs/types';
import {
  CurveSeries,
  GheResultsPayload,
  TargetRockResult
} from '../../@types/gheResults/types';
import {
  GheAnalogueTablesPayload,
  TargetRockAnalogueTable
} from '../../@types/gheAnalogueTables/types';
import { GheTargetAnalogueTable } from '../gheAnalogueTables/GheTargetAnalogueTable';
import styles from './GheDataView.module.css';

const ROCK_IDS: RockId[] = [
  'GHE1',
  'GHE2',
  'GHE3',
  'GHE4',
  'GHE5',
  'GHE6',
  'GHE7',
  'GHE8',
  'GHE9',
  'GHE10'
];

const NA = '—';

const na = () => <span className={styles.na}>{NA}</span>;

const fmt = (value?: number | null, decimals = 4): React.ReactNode => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return na();
  }
  let text = Number(value).toFixed(decimals);
  if (text.indexOf('.') >= 0) {
    text = text.replace(/0+$/, '').replace(/\.$/, '');
  }
  return text;
};

type RockContext = {
  rockId: RockId;
  input?: TargetRockInput;
  conditions?: ReservoirConditions;
  prediction?: Record<string, number>;
  result?: TargetRockResult;
  analogueTable?: TargetRockAnalogueTable;
};

const curveByLabel = (
  result: TargetRockResult | undefined,
  label: string
): CurveSeries | undefined =>
  [...(result?.krel_curves ?? []), ...(result?.pc_curves ?? [])].find(
    curve => curve.label === label
  );

const arrayCell = (values?: number[]): React.ReactNode => {
  if (!values || values.length === 0) {
    return na();
  }
  const text = values
    .map(value => {
      let formatted = Number(value).toFixed(4);
      if (formatted.indexOf('.') >= 0) {
        formatted = formatted.replace(/0+$/, '').replace(/\.$/, '');
      }
      return formatted;
    })
    .join(', ');
  return <span className={styles.arrayCell}>{text}</span>;
};

type KvRow = { label: string; render: (ctx: RockContext) => React.ReactNode };
type KvSection = { title: string; kind: 'kv'; rows: KvRow[] };
type AnalogueSection = { title: string; kind: 'analogue' };
type Section = KvSection | AnalogueSection;

const pred = (ctx: RockContext, key: string) => fmt(ctx.prediction?.[key]);
const yValues = (ctx: RockContext, label: string) =>
  arrayCell(curveByLabel(ctx.result, label)?.y_values);

const SECTIONS: Section[] = [
  {
    title: '4.2 Fluid properties',
    kind: 'kv',
    rows: [
      { label: 'NaCl molality', render: () => na() },
      { label: 'MgCl2 molality', render: () => na() },
      { label: 'IFT (mN/m)', render: ctx => pred(ctx, 'ift_mn_m') },
      { label: 'Brine viscosity', render: () => na() },
      { label: 'CO2 viscosity', render: () => na() },
      { label: 'CO2 density', render: () => na() },
      { label: 'Viscosity ratio', render: () => na() }
    ]
  },
  {
    title: '4.3 Rock features',
    kind: 'kv',
    rows: [
      { label: 'PT ratio', render: () => na() },
      { label: 'RQI', render: () => na() },
      { label: 'FZI', render: () => na() },
      { label: 'Capillary number (ML)', render: () => na() }
    ]
  },
  {
    title: '4.4 Machine Learning',
    kind: 'kv',
    rows: [
      { label: 'PowerTransformer', render: () => na() },
      { label: 'GaussianMixture', render: () => na() },
      { label: 'PolynomialFeatures', render: () => na() },
      { label: 'LinearRegression', render: () => na() },
      { label: 'RidgeCV', render: () => na() },
      { label: 'Swi', render: ctx => pred(ctx, 'swi') },
      { label: 'Sco2i', render: ctx => pred(ctx, 'sco2i') },
      { label: 'KrCO2@Swi', render: ctx => pred(ctx, 'krco2_swi') },
      { label: 'Krw@Sco2i', render: ctx => pred(ctx, 'krw_sco2i') },
      { label: 'Corey exp. CO2 (drainage)', render: ctx => pred(ctx, 'co2_exp') },
      { label: 'Corey exp. water (drainage)', render: ctx => pred(ctx, 'water_exp') },
      { label: 'Corey exp. CO2 (imbibition)', render: () => na() },
      { label: 'Corey exp. water (imbibition)', render: () => na() },
      { label: 'Pe (kPa)', render: ctx => pred(ctx, 'pe_kpa') },
      { label: 'Lambda_BC', render: ctx => pred(ctx, 'lambda_bc') }
    ]
  },
  {
    title: '4.5 ML curve equations',
    kind: 'kv',
    rows: [
      {
        label: 'Swd (drainage saturation)',
        render: ctx => arrayCell(curveByLabel(ctx.result, 'krco2 drainage ML')?.x_values)
      },
      { label: 'KrCO2_drain', render: ctx => yValues(ctx, 'krco2 drainage ML') },
      { label: 'Krw_drain', render: ctx => yValues(ctx, 'krw drainage ML') },
      {
        label: 'Swn (imbibition saturation)',
        render: ctx => arrayCell(curveByLabel(ctx.result, 'krco2 imbibition ML')?.x_values)
      },
      { label: 'KrCO2_imb', render: ctx => yValues(ctx, 'krco2 imbibition ML') },
      { label: 'Krw_imb', render: ctx => yValues(ctx, 'krw imbibition ML') },
      { label: 'Pc_kPa', render: ctx => yValues(ctx, 'pc ML') },
      { label: 'Pc_bar', render: () => na() }
    ]
  },
  { title: '4.6 Analogue path', kind: 'analogue' },
  {
    title: '4.7 Page 2 curves',
    kind: 'kv',
    rows: [
      { label: 'krco2 drainage ML', render: ctx => yValues(ctx, 'krco2 drainage ML') },
      { label: 'krw drainage ML', render: ctx => yValues(ctx, 'krw drainage ML') },
      { label: 'krco2 imbibition ML', render: ctx => yValues(ctx, 'krco2 imbibition ML') },
      { label: 'krw imbibition ML', render: ctx => yValues(ctx, 'krw imbibition ML') },
      { label: 'pc ML', render: ctx => yValues(ctx, 'pc ML') },
      { label: 'krco2 drainage Analogue', render: ctx => yValues(ctx, 'krco2 drainage Analogue') },
      { label: 'krw drainage Analogue', render: ctx => yValues(ctx, 'krw drainage Analogue') },
      { label: 'krco2 imbibition Analogue', render: ctx => yValues(ctx, 'krco2 imbibition Analogue') },
      { label: 'krw imbibition Analogue', render: ctx => yValues(ctx, 'krw imbibition Analogue') },
      { label: 'pc Analogue', render: ctx => yValues(ctx, 'pc Analogue') }
    ]
  }
];

const isValidRock = (rock: TargetRockInput) =>
  Number.isFinite(rock.porosity) &&
  Number.isFinite(rock.permeability_md) &&
  rock.porosity > 0 &&
  rock.permeability_md > 0;

export const GheDataView = () => {
  const { selectedInfo } = useSelected();
  const { configStates } = useAppContext();
  const projectUid = selectedInfo.project?.uid;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<GheInputSession | null>(null);
  const [results, setResults] = useState<GheResultsPayload | null>(null);
  const [analogues, setAnalogues] = useState<GheAnalogueTablesPayload | null>(null);
  const [predictions, setPredictions] = useState<Record<string, Record<string, number>>>({});
  const [activeRockId, setActiveRockId] = useState<string>('');

  useEffect(() => {
    if (!projectUid) {
      setSession(null);
      return;
    }

    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const loadedSession = await getGheInputs(projectUid);
        if (!isMounted) return;
        if (!loadedSession || (loadedSession.target_rocks ?? []).length === 0) {
          setSession(null);
          return;
        }
        setSession(loadedSession);

        const conditions = loadedSession.reservoir_conditions;
        const unseenSamples = loadedSession.target_rocks.filter(isValidRock).map(rock => ({
          sample_id: `${rock.rock_id.toLowerCase()}-input`,
          rock_id: rock.rock_id,
          porosity: rock.porosity,
          permeability_md: rock.permeability_md,
          pressure_mpa: conditions.pressure_mpa,
          temperature_c: conditions.temperature_c,
          nacl_ppm: conditions.nacl_ppm,
          mgcl2_ppm: conditions.mgcl2_ppm
        }));

        const [loadedResults, loadedAnalogues, inference] = await Promise.all([
          getGheResults(projectUid).catch(() => null),
          getGheAnalogueTables(projectUid).catch(() => null),
          unseenSamples.length > 0
            ? runGheUnseenInference(projectUid, {
                samples: unseenSamples,
                include_pca_diagnostics: false,
                include_plot_payload: false
              }).catch(() => null)
            : Promise.resolve(null)
        ]);
        if (!isMounted) return;

        setResults(loadedResults);
        setAnalogues(loadedAnalogues);
        const predictionMap: Record<string, Record<string, number>> = {};
        inference?.predictions?.forEach(prediction => {
          predictionMap[prediction.rock_id] = prediction.predictions;
        });
        setPredictions(predictionMap);
      } catch (err) {
        if (!isMounted) return;
        setError('Unable to load GHE data. Please verify GHE inputs and try again.');
        setSession(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [projectUid, configStates.refreshCounter]);

  const availableRockIds = useMemo(
    () =>
      ROCK_IDS.filter(rockId =>
        (session?.target_rocks ?? []).some(rock => rock.rock_id === rockId)
      ),
    [session]
  );

  useEffect(() => {
    if (availableRockIds.length === 0) {
      setActiveRockId('');
      return;
    }
    if (!availableRockIds.includes(activeRockId as RockId)) {
      setActiveRockId(availableRockIds[0]);
    }
  }, [availableRockIds, activeRockId]);

  const buildContext = (rockId: RockId): RockContext => ({
    rockId,
    input: session?.target_rocks.find(rock => rock.rock_id === rockId),
    conditions: session?.reservoir_conditions,
    prediction: predictions[rockId],
    result: (results?.results ?? results?.target_rocks ?? []).find(
      item => item.rock_id === rockId
    ),
    analogueTable: (analogues?.tables ?? []).find(table => table.rock_id === rockId)
  });

  const renderSection = (section: Section, ctx: RockContext) => {
    if (section.kind === 'analogue') {
      return (
        <div key={section.title} className={styles.section}>
          <h4 className={styles.sectionTitle}>{section.title}</h4>
          {ctx.analogueTable && ctx.analogueTable.rows.length > 0 ? (
            <GheTargetAnalogueTable table={ctx.analogueTable} />
          ) : (
            na()
          )}
        </div>
      );
    }

    return (
      <div key={section.title} className={styles.section}>
        <h4 className={styles.sectionTitle}>{section.title}</h4>
        <table className={styles.kvTable}>
          <tbody>
            {section.rows.map(row => (
              <tr key={row.label}>
                <th scope="row">{row.label}</th>
                <td>{row.render(ctx)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return <ProcessingLoader data-testid="ghe-data-loading" />;
  }

  if (error) {
    return <Alert type="error" showIcon message={error} data-testid="ghe-data-error" />;
  }

  if (!session || availableRockIds.length === 0) {
    return (
      <Alert
        type="info"
        showIcon
        data-testid="ghe-data-empty"
        message='Save GHE inputs to see the target rock data.'
      />
    );
  }

  return (
    <div data-testid="ghe-data-view">
      <Tabs value={activeRockId} onChange={setActiveRockId}>
        <TabList>
          {availableRockIds.map(rockId => (
            <Tab key={rockId} value={rockId}>
              {rockId}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {availableRockIds.map(rockId => {
            const ctx = buildContext(rockId);
            return (
              <TabPanel key={rockId} value={rockId}>
                <div className={styles.view}>
                  {SECTIONS.map(section => renderSection(section, ctx))}
                </div>
              </TabPanel>
            );
          })}
        </TabPanels>
      </Tabs>
    </div>
  );
};

export default GheDataView;
